/**
 * AdminImmeublePortalMirror — Batch #14.102
 *
 * Vue admin "miroir du portail client" affichée dans la tuile « Vue portail ».
 *
 * UX : 6 cartes KPI cliquables en haut. Le clic sur une carte affiche la liste
 * détaillée correspondante dans un panneau en dessous (drill-down par carte).
 *
 *   - Immeubles  → noms + adresses
 *   - Logements  → tous les logements (occupé/vacant + loyer)
 *   - Locataires → noms + emails + balance
 *   - Tickets    → tickets ouverts avec priorité
 *   - Revenu mensuel    → loyer mensuel par logement actif
 *   - Revenu annuel projeté → 12× loyer mensuel par logement actif
 *
 * Données : /api/immeuble/overview-global
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Building2, Home, Users, AlertCircle, TrendingUp, DollarSign, Eye, RefreshCw, ChevronUp, ChevronDown, BarChart3, Calculator } from "lucide-react";
import { EditBuildingModal, EditUnitModal, EditTenantModal } from "./MirrorEditModals";
import { ClickableStat } from "./mirrorPanels/_shared";
import { BuildingsPanel } from "./mirrorPanels/BuildingsPanel";
import { UnitsPanel } from "./mirrorPanels/UnitsPanel";
import { TenantsPanel } from "./mirrorPanels/TenantsPanel";
import { TicketsPanel, RevenuePanel } from "./mirrorPanels/TicketsRevenuePanels";
import { TalCalculator } from "@/components/TalCalculator";
import { RentArrearsBoard } from "@/components/RentArrearsBoard";
import { fmtCAD as fmtMoney } from "@/lib/format";


export default function AdminImmeublePortalMirror({ masterId } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null); // "buildings" | "units" | "tenants" | "tickets" | "monthly" | "annual"
  // Batch #14.218 — Edit states pour modals admin
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [editingUnit, setEditingUnit] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  // Batch #14.219 — Create states pour modals admin (ajout au nom du client)
  const [creating, setCreating] = useState(null); // "building" | "unit" | "tenant" | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = masterId
        ? `/immeuble/overview-global?master_id=${encodeURIComponent(masterId)}`
        : "/immeuble/overview-global";
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      console.error("[mirror] load failed", e);
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  // Index utiles
  const { buildings, units, tenants, openTickets, leases, stats, buildingsById, unitsById, activeLeasesByUnit, activeTenantsByUnit, tenantById, unitsWithActiveLeaseInfo, primaryClientId } = useMemo(() => {
    if (!data) return {
      buildings: [], units: [], tenants: [], openTickets: [], leases: [], stats: {},
      buildingsById: {}, activeLeasesByUnit: {}, tenantById: {}, unitsWithActiveLeaseInfo: [],
      primaryClientId: null,
    };
    const buildings = data.buildings || [];
    const units = data.units || [];
    const tenants = data.tenants || [];
    const openTickets = data.open_tickets || [];
    const leases = data.leases || [];
    const stats = data.stats || {};
    const buildingsById = Object.fromEntries(buildings.map(b => [b.id, b]));
    const unitsById = Object.fromEntries(units.map(u => [u.id, u]));
    const activeLeasesByUnit = {};
    for (const L of leases) {
      if (L.status === "active") activeLeasesByUnit[L.unit_id] = L;
    }
    const tenantById = Object.fromEntries(tenants.map(t => [t.id, t]));
    // Batch #14.182 — Locataires actifs groupés par logement (source de vérité = tenants.unit_id)
    const activeTenantsByUnit = {};
    for (const t of tenants) {
      if (t.is_active === false) continue;
      const uid = t.unit_id;
      if (!uid) continue;
      (activeTenantsByUnit[uid] = activeTenantsByUnit[uid] || []).push(t);
    }
    const unitsWithActiveLeaseInfo = units.map(u => ({
      ...u,
      _lease: activeLeasesByUnit[u.id] || null,
      _tenants: activeTenantsByUnit[u.id] || [],  // locataires actifs sur ce logement
      _building: buildingsById[u.property_id] || null,
    }));
    // Batch #14.219 — primary client_id pour les créations admin (immeuble.client_id requis)
    const primaryClientId = data.master?.primary_user_id
      || buildings[0]?.client_id
      || null;
    return { buildings, units, tenants, openTickets, leases, stats,
             buildingsById, unitsById, activeLeasesByUnit, activeTenantsByUnit,
             tenantById, unitsWithActiveLeaseInfo, primaryClientId };
  }, [data]);

  if (!data && loading) {
    return (
      <div className="bg-white border-2 border-indigo-200 rounded-xl p-4 text-sm text-slate-500" data-testid="admin-portal-mirror">
        Chargement de la vue portail…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div
      className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-5 space-y-4"
      data-testid="admin-portal-mirror"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm sm:text-base font-bold text-indigo-900 flex items-center gap-2">
          <Eye className="w-5 h-5" /> Vue portail propriétaire
          <span className="text-[10px] sm:text-xs font-normal text-indigo-600 bg-white/60 px-2 py-0.5 rounded-full">
            édition admin · agit au nom du client
          </span>
        </h3>
        <div className="flex items-center gap-1.5">
          <button onClick={load} disabled={loading}
                  className="text-xs px-2 py-1 rounded-md border border-indigo-200 bg-white/70 hover:bg-white inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="mirror-refresh" title="Recharger">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={() => setCollapsed((c) => !c)}
                  className="text-xs px-2 py-1 rounded-md border border-indigo-200 bg-white/70 hover:bg-white inline-flex items-center gap-1"
                  data-testid="mirror-collapse">
            {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            {collapsed ? "Afficher" : "Masquer"}
          </button>
        </div>
      </div>

      {collapsed && (
        <div className="text-xs text-indigo-700">
          {stats.nb_buildings} immeuble(s) · {stats.nb_units} logement(s) · {stats.occupancy_rate}% occupation · {stats.nb_open_tickets} ticket(s) ouvert(s)
        </div>
      )}

      {!collapsed && (
        <>
          {/* ═══ 4 cartes principales — cliquables ════════════════════════ */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <ClickableStat
              icon={Building2} label="Immeubles" value={stats.nb_buildings} accent="indigo"
              active={selectedTab === "buildings"}
              onClick={() => setSelectedTab(selectedTab === "buildings" ? null : "buildings")}
              testId="mirror-card-buildings"
            />
            <ClickableStat
              icon={Home} label="Logements" value={`${stats.nb_units_occupied}/${stats.nb_units}`}
              sub={`${stats.occupancy_rate}% occupés`} accent="emerald"
              active={selectedTab === "units"}
              onClick={() => setSelectedTab(selectedTab === "units" ? null : "units")}
              testId="mirror-card-units"
            />
            <ClickableStat
              icon={Users} label="Locataires" value={stats.nb_tenants} accent="blue"
              active={selectedTab === "tenants"}
              onClick={() => setSelectedTab(selectedTab === "tenants" ? null : "tenants")}
              testId="mirror-card-tenants"
            />
            <ClickableStat
              icon={AlertCircle} label="Tickets ouverts" value={stats.nb_open_tickets}
              accent={stats.nb_open_tickets > 0 ? "rose" : "slate"}
              active={selectedTab === "tickets"}
              onClick={() => setSelectedTab(selectedTab === "tickets" ? null : "tickets")}
              testId="mirror-card-tickets"
            />
          </div>

          {/* ═══ 2 cartes Revenus — cliquables ════════════════════════════ */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <ClickableStat
              icon={DollarSign} label="Revenu mensuel (baux actifs)"
              value={fmtMoney(stats.monthly_revenue)} accent="emerald"
              active={selectedTab === "monthly"}
              onClick={() => setSelectedTab(selectedTab === "monthly" ? null : "monthly")}
              testId="mirror-card-monthly"
            />
            <ClickableStat
              icon={TrendingUp} label="Revenu annuel projeté"
              value={fmtMoney(stats.annual_revenue)} accent="amber"
              active={selectedTab === "annual"}
              onClick={() => setSelectedTab(selectedTab === "annual" ? null : "annual")}
              testId="mirror-card-annual"
            />
          </div>

          {/* ═══ Batch #14.231 — Loyers en retard + Calculatrice TAL ══════ */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <ClickableStat
              icon={BarChart3} label="Loyers en retard"
              value="Aging" accent="rose"
              active={selectedTab === "arrears"}
              onClick={() => setSelectedTab(selectedTab === "arrears" ? null : "arrears")}
              testId="mirror-card-arrears"
            />
            <ClickableStat
              icon={Calculator} label="Calculatrice TAL"
              value="Outil" accent="blue"
              active={selectedTab === "tal"}
              onClick={() => setSelectedTab(selectedTab === "tal" ? null : "tal")}
              testId="mirror-card-tal"
            />
          </div>

          {/* ═══ Panneau de détails (dynamique selon la carte cliquée) ════ */}
          {selectedTab === null ? (
            <div className="text-xs text-slate-500 italic text-center py-6 bg-white/50 rounded-lg border border-dashed border-indigo-200">
              Cliquez sur une carte ci-dessus pour afficher les détails.
            </div>
          ) : (
            <div className="bg-white/80 border border-indigo-200 rounded-xl p-3 sm:p-4" data-testid={`mirror-panel-${selectedTab}`}>
              {selectedTab === "buildings" && <BuildingsPanel buildings={buildings} onEdit={setEditingBuilding} onCreate={() => setCreating("building")} />}
              {selectedTab === "units" && <UnitsPanel units={unitsWithActiveLeaseInfo} tenantById={tenantById} onReload={load} onEdit={setEditingUnit} onCreate={() => setCreating("unit")} canCreate={buildings.length > 0} />}
              {selectedTab === "tenants" && <TenantsPanel tenants={tenants} activeLeasesByUnit={activeLeasesByUnit} unitsById={unitsById} buildingsById={buildingsById} onEdit={setEditingTenant} onCreate={() => setCreating("tenant")} canCreate={units.length > 0} />}
              {selectedTab === "tickets" && <TicketsPanel tickets={openTickets} buildingsById={buildingsById} />}
              {selectedTab === "monthly" && <RevenuePanel units={unitsWithActiveLeaseInfo} mode="monthly" />}
              {selectedTab === "annual" && <RevenuePanel units={unitsWithActiveLeaseInfo} mode="annual" />}
              {selectedTab === "arrears" && (
                masterId ? (
                  <RentArrearsBoard
                    endpoint={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/pms/rent-arrears?master_id=${encodeURIComponent(masterId)}`}
                    getHeaders={() => {
                      const t = localStorage.getItem("token");
                      return t ? { Authorization: `Bearer ${t}` } : {};
                    }}
                    testIdPrefix="admin-arrears"
                  />
                ) : (
                  <div className="text-xs text-slate-500 italic py-3 text-center">
                    Sélectionnez une fiche client pour afficher les loyers en retard.
                  </div>
                )
              )}
              {selectedTab === "tal" && <TalCalculator testIdPrefix="admin-tal" defaultOpen />}
            </div>
          )}
        </>
      )}

      {/* Batch #14.218 — Modals édition admin (au nom du client) */}
      {editingBuilding && masterId && (
        <EditBuildingModal
          masterId={masterId}
          building={editingBuilding}
          onClose={() => setEditingBuilding(null)}
          onSaved={() => load()}
        />
      )}
      {editingUnit && masterId && (
        <EditUnitModal
          masterId={masterId}
          unit={editingUnit}
          onClose={() => setEditingUnit(null)}
          onSaved={() => load()}
        />
      )}
      {editingTenant && masterId && (
        <EditTenantModal
          masterId={masterId}
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onSaved={() => load()}
        />
      )}
      {/* Batch #14.219 — Modals création admin (au nom du client) */}
      {creating === "building" && masterId && (
        <EditBuildingModal
          masterId={masterId}
          building={null}
          primaryClientId={primaryClientId}
          onClose={() => setCreating(null)}
          onSaved={() => load()}
        />
      )}
      {creating === "unit" && masterId && (
        <EditUnitModal
          masterId={masterId}
          unit={null}
          buildings={buildings}
          onClose={() => setCreating(null)}
          onSaved={() => load()}
        />
      )}
      {creating === "tenant" && masterId && (
        <EditTenantModal
          masterId={masterId}
          tenant={null}
          units={units}
          unitsById={unitsById}
          buildingsById={buildingsById}
          onClose={() => setCreating(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

